import { useEffect, useState } from 'react';
import { Bell, Package, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { OrderStatusTracker } from '@/components/OrderStatusTracker';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

interface Order {
  id: string;
  status: string;
  delivery_option: string;
  total_price: number;
  created_at: string;
  confirmed_at?: string | null;
  preparing_at?: string | null;
  ready_at?: string | null;
  shipped_at?: string | null;
  delivered_at?: string | null;
  completed_at?: string | null;
  tracking_notes?: string | null;
  frame_types: {
    name: string;
    images: string[];
  };
  qr_cards: {
    title: string;
  };
  establishments: {
    name: string;
    city: string;
  };
}

const statusLabels: Record<string, string> = {
  pending: 'En attente',
  confirmed: 'Confirmée',
  preparing: 'En préparation',
  ready_for_pickup: 'Prêt pour retrait',
  shipped: 'Expédiée',
  delivered: 'Livrée',
  completed: 'Terminée',
  cancelled: 'Annulée'
};

export const OrderNotifications = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [lastViewedAt, setLastViewedAt] = useState<string | null>(null);

  useEffect(() => {
    // Load last viewed timestamp from localStorage
    const stored = localStorage.getItem('order_notifications_last_viewed');
    if (stored) {
      setLastViewedAt(stored);
    }
    
    fetchOrders();
    
    // Subscribe to real-time updates
    const channel = supabase
      .channel('order-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'frame_orders'
        },
        () => {
          fetchOrders();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchOrders = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // First, get the user's QR card IDs
      const { data: qrCards, error: qrError } = await supabase
        .from('qr_cards')
        .select('id')
        .eq('user_id', user.id);

      if (qrError) throw qrError;

      if (!qrCards || qrCards.length === 0) {
        setOrders([]);
        setLoading(false);
        return;
      }

      const qrCardIds = qrCards.map(card => card.id);

      // Then get orders for those QR cards
      const { data, error } = await supabase
        .from('frame_orders')
        .select(`
          *,
          frame_types (
            name,
            images
          ),
          qr_cards (
            title,
            user_id
          ),
          establishments (
            name,
            city
          )
        `)
        .in('qr_card_id', qrCardIds)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      setOrders(data || []);
      
      // Calculate unread count based on last viewed timestamp
      calculateUnreadCount(data || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateUnreadCount = (ordersList: Order[]) => {
    // Get last viewed timestamp from state or localStorage
    const lastViewed = lastViewedAt || localStorage.getItem('order_notifications_last_viewed');
    
    if (!lastViewed) {
      // If never viewed, count all active orders
      const activeOrders = ordersList.filter(
        order => !['pending', 'completed', 'cancelled'].includes(order.status)
      );
      setUnreadCount(activeOrders.length);
      return;
    }

    // Count orders that were updated after last view
    const lastViewedDate = new Date(lastViewed);
    const newOrders = ordersList.filter(order => {
      if (['pending', 'completed', 'cancelled'].includes(order.status)) {
        return false;
      }
      
      // Check if any status timestamp is newer than last viewed
      const timestamps = [
        order.confirmed_at,
        order.preparing_at,
        order.ready_at,
        order.shipped_at,
        order.delivered_at,
        order.completed_at
      ].filter(Boolean);

      return timestamps.some(timestamp => 
        timestamp && new Date(timestamp) > lastViewedDate
      );
    });

    setUnreadCount(newOrders.length);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      // Mark all notifications as read when opening
      const now = new Date().toISOString();
      localStorage.setItem('order_notifications_last_viewed', now);
      setLastViewedAt(now);
      setUnreadCount(0);
    }
  };

  if (loading) return null;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-10 w-10 rounded-full hover:bg-primary/10"
        >
          <Bell className="h-5 w-5 text-foreground" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs rounded-full animate-pulse"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent 
        align="end" 
        className="w-[95vw] sm:w-[440px] p-0 bg-background border-border/50 soft-shadow-lg z-[100]"
        sideOffset={8}
      >
        <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 border-b border-border/50 bg-background sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <Package className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
            <h3 className="font-semibold text-xs sm:text-sm">Mes commandes</h3>
          </div>
          {unreadCount > 0 && (
            <Badge variant="secondary" className="bg-primary/15 text-primary text-[10px] sm:text-xs px-1.5 sm:px-2 h-5">
              {unreadCount} en cours
            </Badge>
          )}
        </div>

        <ScrollArea className="h-[70vh] sm:h-[500px] max-h-[70vh]">
          {orders.length === 0 ? (
            <div className="p-6 sm:p-8 text-center space-y-2">
              <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto rounded-full bg-muted/50 flex items-center justify-center">
                <Package className="w-5 h-5 sm:w-6 sm:h-6 text-muted-foreground" />
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground">Aucune commande</p>
            </div>
          ) : (
            <div className="divide-y divide-border/50">
              {orders.map((order) => (
                <div key={order.id} className="p-2.5 sm:p-4 hover:bg-muted/30 transition-colors">
                  <div className="space-y-2 sm:space-y-3">
                    {/* Order Header */}
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                        {order.frame_types?.images?.[0] ? (
                          <img 
                            src={order.frame_types.images[0]} 
                            alt={order.frame_types.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-4 h-4 sm:w-6 sm:h-6 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0 space-y-0.5 sm:space-y-1">
                        <p className="text-xs sm:text-sm font-medium text-foreground line-clamp-1">
                          {order.qr_cards?.title || 'Sans titre'}
                        </p>
                        <p className="text-[10px] sm:text-xs text-muted-foreground line-clamp-1">
                          {order.frame_types?.name}
                        </p>
                        <p className="text-[10px] sm:text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(order.created_at), {
                            addSuffix: true,
                            locale: fr
                          })}
                        </p>
                      </div>

                      <Badge 
                        variant={order.status === 'cancelled' ? 'destructive' : 'secondary'}
                        className="text-[10px] sm:text-xs flex-shrink-0 px-1.5 sm:px-2 h-5 sm:h-auto"
                      >
                        {statusLabels[order.status] || order.status}
                      </Badge>
                    </div>

                    {/* Order Status Tracker (compact view) */}
                    {order.status !== 'cancelled' && order.status !== 'pending' && (
                      <>
                        <Separator className="my-1.5 sm:my-2" />
                        <div className="pl-0.5 sm:pl-1 scale-[0.85] sm:scale-90 origin-left">
                          <OrderStatusTracker
                            status={order.status as any}
                            deliveryOption={order.delivery_option || 'delivery'}
                            confirmedAt={order.confirmed_at}
                            preparingAt={order.preparing_at}
                            readyAt={order.ready_at}
                            shippedAt={order.shipped_at}
                            deliveredAt={order.delivered_at}
                            completedAt={order.completed_at}
                          />
                        </div>
                      </>
                    )}

                    {/* Establishment Info */}
                    {order.establishments && (
                      <>
                        <Separator className="my-1.5 sm:my-2" />
                        <div className="flex items-center justify-between text-[10px] sm:text-xs gap-2">
                          <span className="text-muted-foreground truncate">
                            {order.establishments.name}
                          </span>
                          <span className="text-muted-foreground flex-shrink-0">
                            {order.establishments.city}
                          </span>
                        </div>
                      </>
                    )}

                    {/* Tracking Notes */}
                    {order.tracking_notes && (
                      <div className="mt-1.5 sm:mt-2 p-1.5 sm:p-2 rounded-lg bg-muted/50">
                        <p className="text-[10px] sm:text-xs text-muted-foreground">
                          📝 {order.tracking_notes}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {orders.length > 0 && (
          <div className="border-t border-border/50 p-1.5 sm:p-2 bg-background sticky bottom-0">
            <Button
              variant="ghost"
              className="w-full text-[10px] sm:text-xs gap-1 sm:gap-2 hover:bg-primary/10 hover:text-primary h-8 sm:h-9"
              onClick={() => {
                setOpen(false);
                window.location.href = '/my-orders';
              }}
            >
              Voir toutes les commandes
              <ChevronRight className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
